import Pusher from 'pusher-js';
import { NotifyManager } from 'notify-component';
import * as jobsActions from '../actions/jobs';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';
import { ActiveConstants } from 'finhelper';
import {
  FAILED,
  FINISHED,
  getTextByType,
  PENDING,
  PROCESSING,
  STARTED,
} from '#app/middlewares/utils/webSocketHelper';
import { invalidateByScope } from '#app/helpers/invalidateByScope';
import refreshAllUserData from '#app/helpers/refreshAllUserData';
import { InvalidationScope } from '#app/constants/invalidationScopes';
import TaskTypes from '#app/constants/TaskTypes';
// Временно отключено: точечное обновление invest-grid по ACTIVES_UPDATED (WS-1360).
// import {
//   extractActiveChangesFromJob,
//   updateInvestsCacheByIds,
// } from '#app/helpers/invests/updateInvestsCacheByIds';

const WS_JOB_QUEUE_NOTIFY_SUFFIX = '__ws_queue';
const wsJobQueueNotifyId = (jobId) => `${jobId}${WS_JOB_QUEUE_NOTIFY_SUFFIX}`;

async function pending(store, job) {
  let state = store.getState();

  if (typeof state.jobs[job.job_id] === 'undefined') {
    NotifyManager.loadingOnce(
      wsJobQueueNotifyId(job.job_id),
      '',
      getTextByType(job.type_id) + ', задача в очереди.'
    );
    store.dispatch(jobsActions.handleCreateJob(job.job_id));
  }
}

async function started(store, job) {
  let state = store.getState();
  const label = getTextByType(job.type_id);

  if (typeof state.jobs[job.job_id] === 'undefined') {
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.loadingOnce(job.job_id, '', label);
    store.dispatch(jobsActions.handleCreateJob(job.job_id));
  } else {
    // Replace queue text with working toast lifecycle.
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.loadingOnce(job.job_id, '', label);
  }

  if (job.percent !== undefined && job.percent !== null) {
    NotifyManager.update(job.job_id, parseFloat(job.percent));
  }
}

async function failed(store, job) {
  let state = store.getState();

  if (typeof state.jobs[job.job_id] !== 'undefined') {
    NotifyManager.error('Ошибка', 'Произошла ошибка при выполнении операции');
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.delete(job.job_id);
    store.dispatch(jobsActions.handleRemoveJob(job.job_id));
  }
}

async function processing(store, job) {
  let state = store.getState();
  const label = getTextByType(job.type_id);

  if (typeof state.jobs[job.job_id] === 'undefined') {
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.loadingOnce(job.job_id, '', label);
    store.dispatch(jobsActions.handleCreateJob(job.job_id));
  } else {
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.loadingOnce(job.job_id, '', label);
  }

  if (job.percent !== undefined && job.percent !== null) {
    NotifyManager.update(job.job_id, parseFloat(job.percent));
  }
}

async function finished(store, job, queryClient) {
  let state = store.getState();

  if (typeof state.jobs[job.job_id] !== 'undefined') {
    // console.log('[ws-obligations-debug] finished-job', {
    //   job_id: job?.job_id,
    //   type_id: job?.type_id,
    //   status: job?.status,
    //   percent: job?.percent,
    //   type_text: getTextByType(job?.type_id),
    //   payload: job,
    // });

    NotifyManager.info('Успешно', 'Операция завершена');
    NotifyManager.delete(wsJobQueueNotifyId(job.job_id));
    NotifyManager.delete(job.job_id);
    store.dispatch(jobsActions.handleRemoveJob(job.job_id));

    if (job.type_id === TaskTypes.GOAL) {
      if (queryClient) {
        await invalidateByScope(InvalidationScope.PLAN_SAVE_QUERY_CACHE, {
          queryClient,
          dispatch: store.dispatch,
        });
      }
    }

    // Автоматическое обновление данных после завершения синхронизации Тинькофф
    if (job.type_id === ActiveConstants.TINKOFF) {
      const client = state.interfaceComponents?.client;
      if (client?.id && queryClient) {
        await invalidateByScope(InvalidationScope.BROKER_SYNC, {
          queryClient,
          dispatch: store.dispatch,
          params: { clientId: client.id },
        });
      }
    }

    if (job.type_id === TaskTypes.BCS_API) {
      const client = state.interfaceComponents?.client;
      if (client?.id && queryClient) {
        await invalidateByScope(InvalidationScope.BROKER_SYNC, {
          queryClient,
          dispatch: store.dispatch,
          params: { clientId: client.id },
        });
      }
    }
    // Синхронизация Freedom Broker (TaskTypes.FREEDOM_REPORT — см. webSocketHelper getTextByType)
    if (job.type_id === TaskTypes.FREEDOM_REPORT) {
      const client = state.interfaceComponents?.client;
      if (client?.id && queryClient) {
        await invalidateByScope(InvalidationScope.BROKER_SYNC, {
          queryClient,
          dispatch: store.dispatch,
          params: { clientId: client.id },
        });
      }
    }

    // Автоматическое обновление данных после завершения синхронизации Зенмани (доходы, расходы, операции, уведомления)
    if (job.type_id === ActiveConstants.ZENMONEY) {
      const client = state.interfaceComponents?.client;
      if (client?.id && queryClient) {
        await invalidateByScope(InvalidationScope.ZENMONEY_SYNC, {
          queryClient,
          dispatch: store.dispatch,
          params: { clientId: client.id },
        });
      }
    }

    // После завершения парсинга брокерского отчёта (АТОН, БКС) — один scope: список файлов + инвестиции + уведомления
    if (
      job.type_id === ActiveConstants.ATON_FILE_PARSE ||
      job.type_id === ActiveConstants.ATON_CRM_PARSE ||
      job.type_id === ActiveConstants.BCS_FILE_PARSE
    ) {
      const client = state.interfaceComponents?.client;
      if (client?.id && queryClient) {
        await invalidateByScope(InvalidationScope.BROKER_REPORT_FILES, {
          queryClient,
          dispatch: store.dispatch,
          params: { clientId: client.id },
        });
      }
    }

    // Полное восстановление данных после возврата из точки:
    // очищаем TanStack cache и перезапрашиваем данные через scope/Redux.
    if (job.type_id === TaskTypes.RETURN_ACTIVES_BACKUP) {
      const client = state.interfaceComponents?.client;
      await refreshAllUserData({
        queryClient,
        dispatch: store.dispatch,
        client,
      });
    }

    // Временно отключено: точечное обновление инвестиций по ACTIVES_UPDATED (payload activeIds / active_changes).
    // if (job.type_id === TaskTypes.ACTIVES_UPDATED) {
    //   const activeChanges = extractActiveChangesFromJob(job);
    //   if (activeChanges.length && queryClient) {
    //     const { requiresTradeLikeInvalidation } = await updateInvestsCacheByIds({
    //       queryClient,
    //       activeIds: activeChanges,
    //     });
    //     if (requiresTradeLikeInvalidation) {
    //       // Если точечное обновление небезопасно (нет кэша или пришли новые id),
    //       // делаем широкий сценарий обновления как при создании/редактировании сделки.
    //       await invalidateByScope(InvalidationScope.TRADE, {
    //         queryClient,
    //         dispatch: store.dispatch,
    //       });
    //     }
    //     await invalidateByScope(InvalidationScope.INVESTS_ACTIVES_UPDATED, {
    //       queryClient,
    //       dispatch: store.dispatch,
    //       params: { activeIds: activeChanges.map((item) => item.id) },
    //     });
    //   }
    // }
  }
}

const eventQueue = [];
let isProcessingQueue = false;
let queryClientInstance = null;

export function enqueueEvent(store, job) {
  return new Promise((resolve) => {
    eventQueue.push({ store, job, resolve });
    processQueue();
  });
}

async function processQueue() {
  if (isProcessingQueue) return;
  isProcessingQueue = true;

  while (eventQueue.length > 0) {
    const { store, job, resolve } = eventQueue.shift();

    if (job.status !== undefined && job.status !== null) {
      switch (job.status) {
        case PENDING:
          await pending(store, job);
          break;
        case STARTED:
          await started(store, job);
          break;
        case PROCESSING:
          await processing(store, job);
          break;
        case FAILED:
          await failed(store, job);
          break;
        case FINISHED:
          await finished(store, job, queryClientInstance);
          break;
      }
    }
    resolve();
  }

  isProcessingQueue = false;
}

const socketMiddleware = (queryClient) => {
  queryClientInstance = queryClient;
  let pusher = null;
  let jobsChannel = null;

  return (store) => (next) => (action) => {
    switch (action.type) {
      case 'SOCKETS_CONNECTING': {
        const api_token = localStorage.getItem(LocalStorageHelper.API_TOKEN);
        const headers = { Authorization: api_token };

        if (!pusher) {
          pusher = new Pusher('d80a938aded21b41c19d', {
            cluster: 'eu',
            channelAuthorization: {
              endpoint: process.env.REACT_APP_API_URL + '/broadcasting/auth',
              transport: 'ajax',
              headers: headers,
              customHandler: null,
            },
          });

          jobsChannel = pusher.subscribe(
            'private-jobs-' +
              process.env.REACT_APP_ENV +
              '-' +
              action.payload.role +
              '-' +
              action.payload.userId
          );
          jobsChannel.bind('jobs', (job) => {
            // eslint-disable-next-line no-console -- trace every job event from Pusher
            console.log('[ws-jobs]', {
              job_id: job?.job_id,
              type_id: job?.type_id,
              status: job?.status,
              percent: job?.percent,
              type_text: getTextByType(job?.type_id),
              raw: job,
            });
            enqueueEvent(store, job);
          });
        }

        break;
      }

      case 'SOCKETS_DISCONNECTING':
        if (pusher) {
          if (jobsChannel) {
            jobsChannel.unbind_all();
            jobsChannel?.unsubscribe();
            jobsChannel = null;
          }
          pusher.disconnect();
          pusher = null;
        }
        break;

      default:
        return next(action);
    }
  };
};

export default socketMiddleware;
